import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import Big from 'big.js'
import {countries, type TCountryCode} from 'countries-list'
import {Breadcrumb, Breadcrumbs} from 'react-aria-components'

import successSvg from '@/assets/icons/success.svg'
import spinnerLottie from '@/assets/lotties/spinner.lottie'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import SkipLink from '@/components/SkipLink'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import type {GrubhubOrder} from '@/lib/food-grubhub/foodGrubhubModel'
import useGrubhubGetFoodOrder from '@/lib/food-grubhub/hooks/useGrubhubGetFoodOrder'
import HeadTags from '@/lib/head/HeadTags'
import useCashback from '@/lib/payment/hooks/useCashback'
import {
  FoodGrubhubCartRoute,
  FoodGrubhubPaymentSuccessRoute,
  FoodGrubhubRoute,
} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {formatLocaleDateTime} from '@/utils/datetime'
import RACLink from '@/utils/router/RACLink'
import NotFound from '@/views/NotFound/NotFound'

import FoodGrubhubOrderItemGroups from './components/FoodGrubhubOrderItemGroups'
import style from './FoodGrubhubPaymentResult.module.scss'

function Result({order}: {order: GrubhubOrder}) {
  const navigate = useNavigate()

  const onDone = useCallback(() => {
    void navigate({
      to: FoodGrubhubRoute.fullPath,
    })
  }, [navigate])

  const subtotal = Big(order.charges.lines.dinerTotal)
  const discountValue = order.moreInfo.amountDiscount
    ? Big(-1).times(Big(order.moreInfo.amountDiscount))
    : Big(0)
  const deliveryFee = Big(order.charges.fees.delivery)
  const serviceFee = Big(order.charges.fees.service)
  const totalBeforeTax = subtotal.add(deliveryFee).add(serviceFee).add(discountValue)
  const tax = Big(order.charges.taxes.total)
  const driverTip = Big(order.moreInfo.driverTip)
  const roundUp = Big(order.moreInfo.amountRoundUp)
  const total = totalBeforeTax.add(tax).add(driverTip).add(roundUp)

  const {data: cashback} = useCashback(total)

  return (
    <div className={style.Result}>
      <div role='alert'>
        <img src={successSvg} alt='' className={style.ResultDecoration} />
        <h1 className={style.Heading}>
          <span role='alert'>Thank you for the order</span>
        </h1>
        <div className={style.Description}>
          We have sent an email to <span className={style.Email}>{order.email}</span> with
          everything you need to know about your order
        </div>
      </div>
      <div className={style.Divider} />
      <div className={style.OrderDetails}>
        <div className={style.Header}>
          <h2>Order details</h2>
          <div className={style.OrderId}>
            <VisuallyHidden>Order ID:</VisuallyHidden>#{order.id}
          </div>
          {/* TODO: copy */}
        </div>
        <div className={style.ShippingInfo}>
          <div className={style.NameAndTime}>
            <div className={style.Name}>
              <VisuallyHidden>Name:</VisuallyHidden>
              {order.shippingInfo.name}
            </div>
            <div className={style.Time}>
              Placed on{' '}
              {formatLocaleDateTime(order.createdAt, 'en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          </div>
          <div className={style.Address}>
            <div className={style.AddressItem}>
              <VisuallyHidden>Address line 1:</VisuallyHidden>
              {order.shippingInfo.line1}
            </div>
            <div className={style.AddressItem}>
              <VisuallyHidden>City:</VisuallyHidden>
              {order.shippingInfo.city}
            </div>
            <div className={style.AddressItem}>
              <VisuallyHidden>State:</VisuallyHidden>
              {order.shippingInfo.state}
            </div>
            <div className={style.AddressItem}>
              <VisuallyHidden>Postal code:</VisuallyHidden>
              {order.shippingInfo.postalCode}
            </div>
            <div className={style.AddressItem}>
              <VisuallyHidden>Country:</VisuallyHidden>
              {countries[order.shippingInfo.country as unknown as TCountryCode].name}
            </div>
          </div>
        </div>
        <FoodGrubhubOrderItemGroups items={order.charges.lines.lineItems} />
      </div>
      <div className={style.Divider} />
      <div className={style.DeliveryInfo}>
        <div className={style.DeliveryItems}>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Subtotal ({order.charges.lines.lineItems.length} items)
              <VisuallyHidden>:</VisuallyHidden>
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price
                price={subtotal}
                options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
              />
            </div>
          </div>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Delivery Fee<VisuallyHidden>:</VisuallyHidden>
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price
                price={deliveryFee}
                options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
              />
            </div>
          </div>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Service Fee<VisuallyHidden>:</VisuallyHidden>
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price
                price={serviceFee}
                options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
              />
            </div>
          </div>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Estimated tax to be collected<VisuallyHidden>:</VisuallyHidden>
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price
                price={tax}
                options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
              />
            </div>
          </div>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Tips<VisuallyHidden>:</VisuallyHidden>
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price
                price={driverTip}
                options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
              />
            </div>
          </div>
          {roundUp.gt(0) && (
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>
                Donate change to NFB<VisuallyHidden>:</VisuallyHidden>
              </div>
              <div className={style.DeliveryItemPrice}>
                <Price
                  price={roundUp}
                  options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
                />
              </div>
            </div>
          )}
          {discountValue.lt(0) && (
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>
                Discount (Total)<VisuallyHidden>:</VisuallyHidden>
              </div>
              <div className={clsx(style.DeliveryItemPrice, style.Discount)}>
                <Price
                  price={discountValue}
                  options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={style.Divider} />
      <div className={style.Total}>
        <span>Total</span>
        <span>
          <Price
            price={total}
            options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
          />
        </span>
      </div>
      {cashback && (
        <div className={clsx(style.DeliveryInfo, style.Rewards)}>
          <div className={style.DeliveryItems}>
            <div className={style.DeliveryItem}>
              <div className={clsx(style.DeliveryItemName, style.Good)}>
                Rewards ({cashback.rate.mul(100).toNumber()}%)<VisuallyHidden>:</VisuallyHidden>
              </div>
              <div className={clsx(style.DeliveryItemPrice, style.Discount)}>
                <Price
                  price={cashback.value}
                  options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <PrimaryButton className={style.DoneBtn} onPress={onDone}>
        Done
      </PrimaryButton>
    </div>
  )
}

export default memo(function FoodGrubhubPaymentResult() {
  const {id} = FoodGrubhubPaymentSuccessRoute.useSearch()

  const {data: order, isLoading} = useGrubhubGetFoodOrder(id)

  if (!isLoading && (!order || typeof order === 'string')) {
    return <NotFound />
  }

  return (
    <>
      <HeadTags title={'Checkout successfully'} />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.Page}>
        <Navbar transparentBg noLogo variant='light' />
        <main className={style.CheckoutPage} {...skipTargetProps('main-content')}>
          <div className={style.CheckoutContent}>
            <div className={style.BreadcrumbsContainer} role='region' aria-label='breadcrumb'>
              <Breadcrumbs className={style.Breadcrumbs}>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink className={style.Link} to={FoodGrubhubRoute.fullPath}>
                    Home
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink className={style.Link} to={FoodGrubhubCartRoute.fullPath}>
                    Cart
                  </RACLink>
                </Breadcrumb>
                <Breadcrumb className={style.Breadcrumb}>
                  <RACLink
                    className={style.Link}
                    to={FoodGrubhubPaymentSuccessRoute.fullPath}
                    search={{id}}
                  >
                    Checkout
                  </RACLink>
                </Breadcrumb>
              </Breadcrumbs>
            </div>
            {isLoading && (
              <div className={style.SpinnerContainer}>
                <DotLottieReact
                  src={spinnerLottie}
                  autoplay
                  loop
                  className={style.Spinner}
                  aria-label='Loading product...'
                  role='progressbar'
                />
              </div>
            )}
            {!isLoading && order && typeof order !== 'string' && <Result order={order} />}
          </div>
        </main>
        <Footer />
      </div>
    </>
  )
})
